import { useState, useEffect } from 'react';
import TranslationText from '@Components/Utilities/TranslationText';
import { useWindowSize } from '@Components/Utilities/WindowSize';

const FacetedSearchRangeField = (props) => {

    const windowSize = useWindowSize();
    const [searching, setSearching] = useState(false);
    const [minimum, setMinimum] = useState(props.RangeInfo.Minimum ?? '');
    const [maximum, setMaximum] = useState(props.RangeInfo.Maximum ?? '');
    const [error, setError] = useState('');

    useEffect(function updateRange() {
        if (!searching) {
            setMinimum(props.RangeInfo.Minimum ?? '');
            setMaximum(props.RangeInfo.Maximum ?? '');
        }
        setSearching(false);
    }, [props.RangeInfo]);

    const validateKey = (e) => {
        const regex = /^[0-9,.]*$/;
        const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
        if (props.AllowNegative) {
            allowedKeys.push('-');
        }

        if (e.key === 'Enter') {
            runSearch();
        } else if (
            (!regex.test(e.key) && !allowedKeys.includes(e.key)) ||
            (props.FacetStep >= 1 && (e.key === '.' || e.key == ',')))
        {
            e.preventDefault();
        } else if ((e.key === '.' || e.key === ',')) {
            // Prevent second decimal point
            const currentValue = e.target.value;
            const decimalSeparator = props?.FormatSeparator == "." ? "," : ".";

            // Check if the current value already contains the decimal separator
            if (currentValue.includes(decimalSeparator)) {
                e.preventDefault();
                return;
            }
        } else if (regex.test(e.key)) {
            // Check if the resulting value would match the step attribute
            if (props.FacetStep && props.FacetStep > 0) {
                const input = e.target;
                const currentValue = input.value;
                const selectionStart = input.selectionStart;
                const selectionEnd = input.selectionEnd;

                // Calculate what the new value would be after the key press
                const newValue = currentValue.substring(0, selectionStart) + e.key + currentValue.substring(selectionEnd);

                // Clean the value (remove formatting)
                let cleanedValue = newValue;
                if (props?.FormatSeparator == ".") {
                    cleanedValue = cleanedValue.replaceAll(".", "").replaceAll(",", ".");
                } else {
                    cleanedValue = cleanedValue.replaceAll(",", "");
                }

                const numericValue = parseFloat(cleanedValue);

                // If it's a valid number, check if it matches the input step attribute
                if (!isNaN(numericValue) && numericValue !== 0) {
                    const divided = numericValue / props.FacetStep;
                    const rounded = Math.round(divided);
                    // Use a tolerance appropriate for decimal arithmetic (1e-10 handles most cases)
                    const isMultipleOfStep = Math.abs(divided - rounded) < 1e-10;

                    // Allow if it's a multiple of step or if user is still typing (incomplete number)
                    if (!isMultipleOfStep && cleanedValue === numericValue.toString()) {
                        e.preventDefault();
                    }
                }
            }
            return;
        } else if (allowedKeys.includes(e.key)) {
            return;
        }
    };

    const getInputClassName = () => {
        return 'range-input' + (props.FormatPrefix ? ' prefix-input' : '') + (props.FormatSuffix ? ' suffix-input' : '');
    };

    const isValid = (minimum, maximum) => {
        if (props?.FormatSeparator == ".") {
            minimum = minimum?.replaceAll(".", "").replaceAll(",", ".");
            maximum = maximum?.replaceAll(".", "").replaceAll(",", ".");
        } else {
            minimum = minimum?.replaceAll(",", "");
            maximum = maximum?.replaceAll(",", "");
        }

        let min = parseFloat(minimum);
        let max = parseFloat(maximum);

        // Check if values are within MinLimit and MaxLimit bounds
        const hasMinLimit = props.MinLimit !== undefined && props.MinLimit !== null;
        const hasMaxLimit = props.MaxLimit !== undefined && props.MaxLimit !== null;

        // Validate min value against limits
        if (!isNaN(min)) {
            if (hasMinLimit && min < props.MinLimit) {
                return false;
            }
            if (hasMaxLimit && min > props.MaxLimit) {
                return false;
            }
        }

        // Validate max value against limits
        if (!isNaN(max)) {
            if (hasMinLimit && max < props.MinLimit) {
                return false;
            }
            if (hasMaxLimit && max > props.MaxLimit) {
                return false;
            }
        }

        if (!isNaN(min) && !isNaN(max)) {
            return (min <= max);
        }
        else if (!isNaN(min)) {
            if (props.AllowNegative) {
                return true;
            }
            else {
                return (min >= 0);
            }
        }
        else if (!isNaN(max)) {
            return (max <= Number.MAX_SAFE_INTEGER);
        }
    }
    
    const runSearch = () => {
        if (minimum || maximum) {
            if (isValid(minimum, maximum)) {
                setError('');
                setSearching(true);
                props.RunSearch({
                    fieldName: props.FieldName,
                    value: minimum + '*' + maximum
                });
            } else {
                if (props.MinLimit !== undefined && props.MaxLimit !== undefined)
                {
                    setError('Invalid search range. Range limits: ' + props.MinLimit + ' to ' + props.MaxLimit);
                }
                else {
                    setError('Invalid search range');
                }
            }
        } else {
            props.ClearFacet(props.FieldName, null);
        }
    };

    return (
        <>
            {error && <div className='error' style={{ fontSize: '12px' }}>{error}</div>}
            <div>
                <div className='faceted-range'>
                    {props.FormatPrefix &&
                        <span className='prefix'>
                            {props.FormatPrefix}
                        </span>}
                    <div className='faceted-range-input-container'>
                        <label htmlFor={'range-input-min-' + props.FieldName} className='visually-hidden'>Min {props.DisplayName}</label>
                        <input
                            id={'range-input-min-' + props.FieldName}
                            value={minimum}
                            onChange={(e) => setMinimum(e.target.value)}
                            disabled={props.showLoadingSpinner}
                            type='text'
                            step={props.FacetStep}
                            onKeyDown={validateKey}
                            placeholder='Min'
                            className={getInputClassName()}
                        />
                    </div>
                    {props.FormatSuffix &&
                        <span className='suffix'>
                            {props.FormatSuffix}
                        </span>}
                    <span className='faceted-range-divider'>-</span>
                    {props.FormatPrefix &&
                        <span className='prefix'>
                            {props.FormatPrefix}
                        </span>}
                    <div className='faceted-range-input-container'>
                        <label htmlFor={'range-input-max-' + props.FieldName} className='visually-hidden'>Max {props.DisplayName}</label>
                        <input
                            id={'range-input-max-' + props.FieldName}
                            value={maximum}
                            onChange={(e) => setMaximum(e.target.value)}
                            disabled={props.showLoadingSpinner}
                            type='text'
                            step={props.FacetStep}
                            onKeyDown={validateKey}
                            placeholder='Max'
                            className={getInputClassName()}
                        />
                    </div>
                    {props.FormatSuffix &&
                        <span className='suffix'>
                            {props.FormatSuffix}
                        </span>}
                </div>
                <button className='ts-button faceted-search' onClick={runSearch}>
                    {windowSize.isMobile ?
                        <TranslationText tKey='sandhills.apply' /> :
                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.search' />}
                </button>
            </div>
        </>
    );
};

export default FacetedSearchRangeField;